import React, { useCallback, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../StaticText/StaticTextContext'
import UserOutline from '../../../../Icon/icons/UserOutline/index'
import ShareNodes from '../../../../Icon/icons/ShareNodes/index'
import EyeOutline from '../../../../Icon/icons/EyeOutline/index'
import StackedCards from '../../../../Icon/icons/StackedCards/index'
import SmartCreate from '../../../../Icon/icons/SmartCreate/index'
import Meditation16 from '../../../../Icon/icons/Meditation16/index'
import Button, { TYPES } from '../../../../Button/index'
import { H3, H4 } from '../../../../Heading/index'
import useCommunityImage from '../../../../../services/community/shared/useCommunityImage'
import { useCommunityModal } from '../../../../../services/community/shared/useCommunityModal'

export interface CommunityProfileWelcomeProps {
  staticText: InternationalStrings
  onDismiss: (permanent: boolean) => void
}

interface FeatureItem {
  key: string
  Icon: React.ComponentType
}

const FEATURE_ITEMS: FeatureItem[] = [
  { key: 'personalizeExperience', Icon: UserOutline },
  { key: 'shareAndConnect', Icon: ShareNodes },
  { key: 'updatePrivacy', Icon: EyeOutline },
  { key: 'watchTogether', Icon: StackedCards },
  { key: 'vibeQuiz', Icon: SmartCreate },
  { key: 'learnArchetype', Icon: Meditation16 },
]

const LEFT_COLUMN = FEATURE_ITEMS.slice(0, 3)
const RIGHT_COLUMN = FEATURE_ITEMS.slice(3)

// Renders a single feature row whose label is resolved via the static-text
// hook. Used in a loop, so it must be its own component (rules of hooks).
interface FeatureRowProps {
  itemKey: string
  Icon: React.ComponentType
}

function FeatureRow ({ itemKey, Icon }: FeatureRowProps) {
  const label = useTranslatedText({ keys: [itemKey] })
  return (
    <div className="community-profile-welcome__option-item">
      <span className="community-profile-welcome__option-icon">
        <Icon />
      </span>
      <span className="community-profile-welcome__option-label">
        {label}
      </span>
    </div>
  )
}

interface CommunityProfileWelcomeContentProps {
  onDismiss: (permanent: boolean) => void
}

function CommunityProfileWelcomeContent ({
  onDismiss,
}: CommunityProfileWelcomeContentProps) {
  const [doNotShow, setDoNotShow] = useState(false)
  const watercolorSrc = useCommunityImage('watercolor.png')

  const titleText = useTranslatedText({ keys: ['title'] })
  const subtitleText = useTranslatedText({ keys: ['subtitle'] })
  const doNotShowAgainText = useTranslatedText({ keys: ['doNotShowAgain'] })
  const gotItText = useTranslatedText({ keys: ['gotIt'] })

  const dialogRef = useRef<HTMLDivElement>(null)

  // Escape always does a temporary dismiss; the "do not show again" choice is
  // only persisted by the explicit Got It click below.
  const handleClose = useCallback(() => {
    onDismiss(false)
  }, [onDismiss])

  // Unified scroll lock (body__modal--open), focus trap, Escape handler,
  // and focus restoration on unmount — same hook VibeQuizModal uses.
  useCommunityModal({ dialogRef, onClose: handleClose })

  const handleGotIt = () => {
    onDismiss(doNotShow)
  }

  const titleId = 'community-profile-welcome-title'

  // Skip rendering during SSR — the modal requires document.body for the portal
  if (typeof document === 'undefined') {
    return null
  }

  const overlay = (
    <div className="community-profile-welcome__overlay">
      <div
        ref={dialogRef}
        className="community-profile-welcome__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="community-profile-welcome__header">
          {watercolorSrc && (
            <img
              className="community-profile-welcome__watercolor"
              src={watercolorSrc}
              alt=""
              aria-hidden="true"
            />
          )}
          <H4
            className="community-profile-welcome__title community-profile-welcome__title--mobile"
            id={titleId}
          >
            {titleText}
          </H4>
          <H3
            className="community-profile-welcome__title community-profile-welcome__title--desktop"
          >
            {titleText}
          </H3>
          <span className="community-profile-welcome__subtitle">
            {subtitleText}
          </span>
        </div>
        <div className="community-profile-welcome__content">
          <div className="community-profile-welcome__options">
            <div className="community-profile-welcome__option-column">
              {LEFT_COLUMN.map((item) => (
                <FeatureRow key={item.key} itemKey={item.key} Icon={item.Icon} />
              ))}
            </div>
            <div className="community-profile-welcome__option-column">
              {RIGHT_COLUMN.map((item) => (
                <FeatureRow key={item.key} itemKey={item.key} Icon={item.Icon} />
              ))}
            </div>
          </div>
          <div className="community-profile-welcome__footer">
            <label className="community-profile-welcome__checkbox">
              <input
                type="checkbox"
                className="community-profile-welcome__checkbox-input"
                checked={doNotShow}
                onChange={(e) => setDoNotShow(e.target.checked)}
              />
              <span className="community-profile-welcome__checkbox-label">
                {doNotShowAgainText}
              </span>
            </label>
            <Button
              type={TYPES.PRIMARY}
              className="community-profile-welcome__button"
              onClick={handleGotIt}
            >
              {gotItText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(overlay, document.body)
}

function CommunityProfileWelcome ({
  staticText,
  onDismiss,
}: CommunityProfileWelcomeProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <CommunityProfileWelcomeContent onDismiss={onDismiss} />
    </StaticTextProvider>
  )
}

export default connectStaticText({ storeKey: 'communityProfileWelcome' })(
  CommunityProfileWelcome,
)
